import { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  createStampReward,
  updateStampReward,
  uploadStoreImage,
  updateStampCard,
  getSettings,
} from "@/lib/api/admin";
import { parseCents } from "@/lib/utils/number";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";
import type { StampReward } from "@/lib/api/types";
import type { RewardPrefill } from "./PointRewardFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextInput } from "@/components/TextInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/Textarea";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { ImageUpload } from "@/components/ImageUpload";

// ─── Stamp Reward Form Dialog ─────────────────────────────────────────────────

function makeStampRewardSchema(maxPriceDollars: number) {
  return z.object({
    name: z
      .string()
      .min(2, "Name must be at least 2 characters.")
      .max(50, "Name must be 50 characters or fewer."),
    description: z.string().max(700, "Description must be 700 characters or fewer."),
    stampsRequired: z.string().refine((v) => {
      const n = parseInt(v, 10);
      return !isNaN(n) && n >= 3;
    }, "Stamps required must be at least 3."),
    price: z
      .string()
      .refine((v) => {
        const n = parseFloat(v);
        return !isNaN(n) && n >= 1;
      }, "Price must be at least $1.00.")
      .refine(
        (v) => parseFloat(v) <= maxPriceDollars,
        `Price cannot exceed $${maxPriceDollars.toLocaleString()}.`
      ),
    status: z.enum(["active", "inactive"]),
  });
}

type StampRewardFormValues = z.infer<ReturnType<typeof makeStampRewardSchema>>;

type StampRewardFormDialogProps = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  priceLimitBypass: boolean;
  storeCategory: string;
  hasStampCard: boolean;
  stampReward?: StampReward;
  prefill?: RewardPrefill;
};

export function StampRewardFormDialog({
  open,
  onClose,
  storeId,
  priceLimitBypass,
  storeCategory,
  hasStampCard,
  stampReward,
  prefill,
}: StampRewardFormDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [imageFile, setImageFile] = useState<File | null>(prefill?.imageFile ?? null);
  const [imageFileDirty, setImageFileDirty] = useState(false);
  const [imageUrl, setImageUrl] = useState(
    stampReward?.image_url ?? (prefill?.imageFile ? undefined : prefill?.imageUrl)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const maxPriceDollars = priceLimitBypass
    ? BYPASS_MAX_PRICE_DOLLARS
    : storeCategory === "nightclub"
      ? (settings?.nightclub_max_price_cents ?? 50000) / 100
      : (settings?.default_max_price_cents ?? 30000) / 100;
  const schema = useMemo(() => makeStampRewardSchema(maxPriceDollars), [maxPriceDollars]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isDirty },
  } = useForm<StampRewardFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: stampReward?.name ?? prefill?.name ?? "",
      description: stampReward?.description ?? prefill?.description ?? "",
      stampsRequired: stampReward ? String(stampReward.stamps_required) : "",
      price: stampReward
        ? (stampReward.price_cents / 100).toFixed(2)
        : prefill
          ? (prefill.priceCents / 100).toFixed(2)
          : "",
      status: stampReward?.status ?? "active",
    },
  });

  const stampsRequiredField = register("stampsRequired");
  const priceField = register("price");

  function tryClose() {
    if (isDirty || imageFileDirty) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: StampRewardFormValues) {
    setIsSubmitting(true);
    try {
      let image_url = imageUrl;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "stamp_reward");
        image_url = res.url;
      }
      const payload: Partial<StampReward> = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        stamps_required: parseInt(values.stampsRequired, 10),
        price_cents: parseCents(values.price),
        status: values.status,
        image_url: image_url ?? "",
      };

      if (stampReward) {
        await updateStampReward(user!, storeId, stampReward.id, payload);
      } else if (!hasStampCard) {
        // First reward ever: create card disabled → attach reward → enable card.
        // A brand-new card has no minimum price yet, so 0 (= none) is sent both times.
        await updateStampCard(user!, storeId, { enabled: false, stampMinPriceCents: 0 });
        await createStampReward(user!, storeId, payload);
        await updateStampCard(user!, storeId, { enabled: true, stampMinPriceCents: 0 });
        void qc.invalidateQueries({ queryKey: ["stamp-card", storeId] });
      } else {
        await createStampReward(user!, storeId, payload);
      }

      await qc.invalidateQueries({ queryKey: ["stamp-rewards", storeId] });
      toast.success(stampReward ? "Stamp reward updated." : "Stamp reward created.");
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {stampReward
                ? "Edit Stamp Reward"
                : prefill
                  ? "Make Stamp Reward"
                  : "New Stamp Reward"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 py-2">
            {/* Name */}
            <TextInput
              id="sr-name"
              label="Name *"
              placeholder="Free Dessert"
              maxLength={50}
              error={errors.name?.message}
              {...register("name")}
            />

            {/* Description */}
            <Textarea
              id="sr-desc"
              label="Description"
              rows={2}
              maxLength={700}
              error={errors.description?.message}
              {...register("description")}
            />

            {/* Image */}
            <ImageUpload
              label="Image"
              aspectRatio="1:1"
              currentUrl={imageUrl}
              initialFile={prefill?.imageFile}
              onFileSelect={(file) => {
                setImageFile(file);
                setImageFileDirty(true);
              }}
              onClear={() => {
                setImageFile(null);
                setImageFileDirty(true);
                setImageUrl(undefined);
              }}
              className="w-44"
            />

            {/* Stamps required */}
            <div className="space-y-1">
              <Label htmlFor="sr-stamps">Stamps required *</Label>
              <Input
                id="sr-stamps"
                type="text"
                inputMode="numeric"
                placeholder="3"
                aria-invalid={!!errors.stampsRequired}
                {...stampsRequiredField}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  stampsRequiredField.onChange(e);
                }}
              />
              {errors.stampsRequired ? (
                <p className="text-xs text-destructive">{errors.stampsRequired.message}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Min 3</p>
              )}
            </div>

            {/* Price */}
            <div className="space-y-1">
              <Label htmlFor="sr-price">Retail price ($) *</Label>
              <Input
                id="sr-price"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                aria-invalid={!!errors.price}
                {...priceField}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
                  priceField.onChange(e);
                }}
              />
              {errors.price ? (
                <p className="text-xs text-destructive">{errors.price.message}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Shown crossed out — retail value of the reward. Max $
                  {maxPriceDollars.toLocaleString()}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label>Status</Label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <SegmentedToggle
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                )}
              />
            </div>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner className="size-4 border-background" />
                ) : stampReward ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
